import { motion } from "framer-motion";
import { type FC, useEffect, useRef, useState } from "react";

interface ActorLevelChipProps {
  level: number;
}

interface TierTone {
  border: string;
  text: string;
}

const TIER_TONES: { max: number; tone: TierTone }[] = [
  {
    max: 0,
    tone: {
      border: "border-slate-300 dark:border-slate-700",
      text: "text-slate-500 dark:text-slate-400",
    },
  },
  {
    max: 2,
    tone: {
      border: "border-sky-500/40 dark:border-sky-400/30",
      text: "text-sky-700 dark:text-sky-300",
    },
  },
  {
    max: 5,
    tone: {
      border: "border-emerald-500/40 dark:border-emerald-400/30",
      text: "text-emerald-700 dark:text-emerald-300",
    },
  },
  {
    max: 9,
    tone: {
      border: "border-amber-500/40 dark:border-amber-400/30",
      text: "text-amber-700 dark:text-amber-300",
    },
  },
  {
    max: Number.POSITIVE_INFINITY,
    tone: {
      border: "border-violet-500/40 dark:border-violet-400/30",
      text: "text-violet-700 dark:text-violet-300",
    },
  },
];

function resolveTone(level: number): TierTone {
  for (const tier of TIER_TONES) {
    if (level <= tier.max) {
      return tier.tone;
    }
  }
  return TIER_TONES[0].tone;
}

/** Per-actor level chip — mirrors ModelChip silhouette so the two sit as
 * siblings on actor identity surfaces. Reads the AMR.evolution counter as a
 * gamified "level": each successful produceSeed reincarnation increments it.
 *
 * Tier progression: slate(0) → sky(1-2) → emerald(3-5) → amber(6-9) →
 * violet(10+). One-shot scale-pulse when the level increments — borrowed from
 * 21st HUD Status' HyperText flash, dialed down to a single value-badge cue.
 */
export const ActorLevelChip: FC<ActorLevelChipProps> = ({ level }) => {
  const tone = resolveTone(level);
  const prevLevel = useRef(level);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (level > prevLevel.current) {
      setFlash(true);
      const timer = setTimeout(() => setFlash(false), 700);
      prevLevel.current = level;
      return () => clearTimeout(timer);
    }
    prevLevel.current = level;
  }, [level]);

  return (
    <motion.span
      animate={flash ? { scale: [1, 1.18, 1] } : { scale: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`inline-flex items-center gap-1 border ${tone.border} ${tone.text} px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest`}
      title={`Level ${level} — actor has reincarnated ${level} time${level === 1 ? "" : "s"} via produceSeed`}
    >
      <span>LV</span>
      <span className="tabular-nums">{level}</span>
    </motion.span>
  );
};
